import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle, Circle, PlayCircle } from "@phosphor-icons/react";

export default function WorkoutDashboard() {
  const [plan, setPlan] = useState(null);
  const [activeDay, setActiveDay] = useState(0);
  const [logs, setLogs] = useState([]);
  const [weights, setWeights] = useState({});

  useEffect(() => {
    api.get("/portal/plan").then(({ data }) => setPlan(data)).catch(() => {});
    api.get("/portal/workout-logs").then(({ data }) => setLogs(data)).catch(() => {});
  }, []);

  if (!plan) {
    return (
      <div data-testid="no-plan-assigned" className="text-center py-24">
        <p className="text-[#71717A]">Your coach hasn't assigned a plan yet. Check back soon!</p>
      </div>
    );
  }

  const day = plan.days[activeDay];
  const today = new Date().toISOString().slice(0, 10);
  const isLogged = (exName) => logs.some((l) => l.date === today && l.day_name === day.day_name && l.exercise_name === exName);

  const logExercise = async (ex) => {
    try {
      const { data } = await api.post("/portal/workout-log", {
        day_name: day.day_name,
        exercise_name: ex.name,
        sets_completed: ex.sets,
        weight_used: weights[ex.name] ? parseFloat(weights[ex.name]) : null,
      });
      setLogs((l) => [...l, data]);
      toast.success(`${ex.name} logged`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to log");
    }
  };

  return (
    <div data-testid="workout-dashboard">
      <h1 className="font-heading text-3xl text-white tracking-tight mb-2">Today's Workout</h1>
      {plan.title && <p className="text-[#A1A1AA] mb-6">{plan.title}</p>}
      {plan.pdf_file_id && (
        <a
          data-testid="workout-plan-pdf-link"
          href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${plan.pdf_file_id}`}
          target="_blank"
          rel="noreferrer"
          className="inline-block mb-6 text-sm text-[#D4AF37] underline"
        >
          View Full Plan PDF ({plan.pdf_filename})
        </a>
      )}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {plan.days.map((d, i) => (
          <button
            key={i}
            data-testid={`workout-day-tab-${i}`}
            onClick={() => setActiveDay(i)}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors ${
              activeDay === i ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#27272A] text-[#A1A1AA]"
            }`}
          >
            {d.day_name}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {day.exercises.map((ex, i) => {
          const done = isLogged(ex.name);
          return (
            <div key={i} data-testid={`workout-exercise-${i}`} className="bg-[#141414] border border-[#27272A] rounded-2xl p-5 flex flex-wrap items-center gap-4">
              <button data-testid={`workout-exercise-check-${i}`} onClick={() => !done && logExercise(ex)}>
                {done ? <CheckCircle size={26} weight="fill" className="text-[#D4AF37]" /> : <Circle size={26} className="text-[#52525B]" />}
              </button>
              <div className="flex-1 min-w-[160px]">
                <p className={`text-white font-semibold ${done ? "line-through opacity-60" : ""}`}>{ex.name}</p>
                <p className="text-xs text-[#71717A]">
                  {ex.sets} sets &times; {ex.reps} reps &middot; {ex.rest_seconds}s rest
                </p>
              </div>
              {ex.video_url && (
                <a href={ex.video_url} target="_blank" rel="noreferrer" data-testid={`workout-exercise-video-${i}`} className="text-[#D4AF37]">
                  <PlayCircle size={24} />
                </a>
              )}
              <input
                data-testid={`workout-exercise-weight-${i}`}
                type="number"
                placeholder="Weight (lbs)"
                value={weights[ex.name] || ""}
                onChange={(e) => setWeights((w) => ({ ...w, [ex.name]: e.target.value }))}
                className="w-28 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-1.5 text-sm text-white"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
